import React, { useState, useCallback } from "react";
import axios from "axios";
import Pagination from "Components/Pagination/Pagination";
import ModalWrapper from "Components/Modal/ModalWrapper";

// ─── Axios instance ───────────────────────────────────────────────────────────
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

// ─── Constants ────────────────────────────────────────────────────────────────
const DESCRIPTION_TRUNCATE_LENGTH = 60;
const BRAND = "#c51369";
const BRAND_DARK = "#8b0e4a";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "—";

const formatDateTime = (iso) =>
  iso
    ? `${formatDate(iso)}, ${new Date(iso).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })}`
    : "—";

const getInitials = (name = "") =>
  name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "?";

// ─── Info Chip ────────────────────────────────────────────────────────────────
const InfoChip = ({ icon, label, value }) => (
  <div className="flex flex-col gap-0.5 rounded-xl border border-gray-100 bg-white p-2.5 shadow-sm sm:p-3">
    <div className="flex items-center gap-1.5">
      <span className="text-sm" style={{ fontSize: 14 }}>
        {icon}
      </span>
      <span className="text-[9px] font-semibold uppercase tracking-widest text-gray-400 sm:text-[10px]">
        {label}
      </span>
    </div>
    <span className="truncate text-xs font-semibold text-gray-800 sm:text-sm">
      {value || "—"}
    </span>
  </div>
);

// ─── Section Divider ──────────────────────────────────────────────────────────
const SectionLabel = ({ children }) => (
  <div className="mb-2.5 flex items-center gap-2 sm:mb-3">
    <span className="h-0.5 w-4 rounded-full" style={{ background: BRAND }} />
    <span
      className="text-[9px] font-bold uppercase tracking-[0.15em] sm:text-[10px]"
      style={{ color: BRAND }}
    >
      {children}
    </span>
    <span className="h-0.5 flex-1 rounded-full bg-gray-100" />
  </div>
);

// ─── Skeleton Loader ──────────────────────────────────────────────────────────
const ModalSkeleton = () => (
  <div className="animate-pulse">
    <div
      className="px-4 pb-5 pt-5 sm:px-6 sm:pb-6 sm:pt-6"
      style={{
        background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`,
      }}
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="h-12 w-12 shrink-0 rounded-2xl bg-white/20 sm:h-14 sm:w-14" />
        <div className="flex-1 space-y-2 pt-1">
          <div className="h-4 w-3/5 rounded bg-white/20" />
          <div className="h-3 w-2/5 rounded bg-white/10" />
          <div className="h-5 w-24 rounded-full bg-white/20" />
        </div>
      </div>
    </div>
    <div className="px-4 py-4 sm:px-6 sm:py-5">
      <div className="mb-4 grid grid-cols-2 gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-14 rounded-xl bg-gray-200" />
        ))}
      </div>
      <div className="space-y-2 rounded-xl bg-gray-100 p-4">
        <div className="h-3 w-1/4 rounded bg-gray-200" />
        <div className="h-3 w-full rounded bg-gray-200" />
        <div className="h-3 w-4/5 rounded bg-gray-200" />
      </div>
    </div>
  </div>
);

// ─── Error State ──────────────────────────────────────────────────────────────
const ModalError = ({ message }) => (
  <div className="flex flex-col items-center gap-3 px-6 py-12 text-center sm:py-14">
    <div
      className="flex h-12 w-12 items-center justify-center rounded-full sm:h-14 sm:w-14"
      style={{ background: "#fff0f5" }}
    >
      <span style={{ fontSize: 22 }}>⚠️</span>
    </div>
    <div>
      <p className="font-semibold text-gray-800">Failed to load details</p>
      <p className="mt-1 text-xs text-gray-500 sm:text-sm">{message}</p>
    </div>
  </div>
);

// ─── Combined Staff + Log Detail Modal Content ───────────────────────────────
const ActivityDetailModal = ({ staffData, logData, loading, error }) => {
  if (loading) return <ModalSkeleton />;
  if (error) return <ModalError message={error} />;
  if (!staffData && !logData) return null;

  const {
    fullName,
    phoneNumber,
    gender,
    isActive,
    lastLogin,
    createdAt,
    rolePermission,
  } = staffData ?? {};

  return (
    <div className="flex flex-col" style={{ maxHeight: "88vh" }}>
      {/* ── Gradient Header ──────────────────────────────────────────────── */}
      {staffData && (
        <div
          className="relative shrink-0 overflow-hidden px-4 pb-4 pt-5 sm:px-6 sm:pb-5 sm:pt-6"
          style={{
            background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`,
          }}
        >
          {/* Decorative blobs */}
          <span className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-white opacity-10 sm:h-28 sm:w-28" />
          <span className="pointer-events-none absolute -bottom-8 left-8 h-16 w-16 rounded-full bg-white opacity-10 sm:h-20 sm:w-20" />

          {/* Leave space for close button (top-right) */}
          <div className="relative flex items-start gap-3 pr-10 sm:gap-4 sm:pr-12">
            {/* Avatar */}
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/20 text-lg font-bold text-white shadow-lg backdrop-blur-sm sm:h-14 sm:w-14 sm:text-xl">
              {getInitials(fullName)}
            </div>

            {/* Name + role + status */}
            <div className="min-w-0 flex-1 pt-0.5">
              <h2 className="truncate text-base font-bold leading-snug text-white sm:text-lg lg:text-xl">
                {fullName ?? "—"}
              </h2>
              <p className="mt-0.5 truncate text-xs text-white/70 sm:text-sm">
                {rolePermission?.rolename ?? "No Role Assigned"}
              </p>

              <span
                className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold ${
                  isActive
                    ? "bg-green-400/20 text-green-100"
                    : "bg-red-300/20 text-red-100"
                }`}
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full ${
                    isActive ? "bg-green-300" : "bg-red-300"
                  }`}
                />
                ROLE STATUS — {isActive ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ── Scrollable Body ──────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
        {/* Staff Info chips */}
        {staffData && (
          <section className="mb-5">
            <SectionLabel>Staff Info</SectionLabel>
            {/* 2 cols on all screens; 3 cols on md+ */}
            <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
              <InfoChip icon="📱" label="Phone" value={phoneNumber} />
              <InfoChip
                icon="🧑"
                label="Gender"
                value={
                  gender
                    ? gender.charAt(0).toUpperCase() + gender.slice(1)
                    : undefined
                }
              />
              <InfoChip
                icon="🔐"
                label="Role"
                value={rolePermission?.rolename}
              />
              <InfoChip
                icon="🟢"
                label="Account Status"
                value={isActive ? "Active" : "Inactive"}
              />
              <InfoChip
                icon="🕐"
                label="Last Login"
                value={formatDateTime(lastLogin)}
              />
              <InfoChip
                icon="📅"
                label="Created On"
                value={formatDate(createdAt)}
              />
            </div>
          </section>
        )}

        {/* Activity Details */}
        {logData && (
          <section>
            <SectionLabel>Activity Details</SectionLabel>
            <div className="space-y-2.5">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <InfoChip
                  icon="⏱️"
                  label="Timestamp"
                  value={formatDateTime(logData.createdAt)}
                />
                {/* Action chip */}
                <div className="flex flex-col gap-0.5 rounded-xl border border-gray-100 bg-white p-2.5 shadow-sm sm:p-3">
                  <div className="flex items-center gap-1.5">
                    <span style={{ fontSize: 14 }}>⚡</span>
                    <span className="text-[9px] font-semibold uppercase tracking-widest text-gray-400 sm:text-[10px]">
                      Action
                    </span>
                  </div>
                  <span className="mt-0.5 inline-flex self-start rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-semibold text-blue-700">
                    {logData.action || "—"}
                  </span>
                </div>
              </div>

              {/* Full description */}
              {logData.description && (
                <div
                  className="rounded-xl border p-3.5 sm:p-4"
                  style={{
                    borderColor: `${BRAND}22`,
                    background: `${BRAND}07`,
                  }}
                >
                  <div className="mb-2 flex items-center gap-1.5">
                    <span style={{ fontSize: 14 }}>📝</span>
                    <span
                      className="text-[9px] font-bold uppercase tracking-widest sm:text-[10px]"
                      style={{ color: BRAND }}
                    >
                      Description
                    </span>
                  </div>
                  <p className="whitespace-pre-wrap text-xs leading-relaxed text-gray-700 sm:text-sm">
                    {logData.description}
                  </p>
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

// ─── Truncated description cell ───────────────────────────────────────────────
const DescriptionCell = ({ description, onReadMore }) => {
  if (!description) return <span className="text-gray-400">—</span>;

  const isTruncated = description.length > DESCRIPTION_TRUNCATE_LENGTH;
  const display = isTruncated
    ? description.slice(0, DESCRIPTION_TRUNCATE_LENGTH).trimEnd() + "…"
    : description;

  return (
    <span>
      {display}
      {isTruncated && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onReadMore();
          }}
          className="ml-1 whitespace-nowrap text-xs font-semibold underline underline-offset-2 group-hover:text-white group-hover:no-underline"
          style={{ color: BRAND }}
        >
          Read more
        </button>
      )}
    </span>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const StaffActivityTable = ({
  activities,
  currentPage,
  totalPages,
  onPageChange,
  loading,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [staffDetail, setStaffDetail] = useState(null);
  const [logDetail, setLogDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const openActivityModal = useCallback(async (activity) => {
    setStaffDetail(null);
    setLogDetail(null);
    setDetailError(null);
    setOpenModal(true);
    setDetailLoading(true);

    try {
      const requests = [];

      if (activity?.id) {
        requests.push(
          api
            .get(`/staffs-activity-log/single-log/${activity.id}`)
            .then((r) => ({ type: "log", data: r.data }))
        );
      } else {
        requests.push(Promise.resolve({ type: "log", data: null }));
      }

      if (activity?.staffId) {
        requests.push(
          api
            .get(`/staff/get-staff/${activity.staffId}`)
            .then((r) => ({ type: "staff", data: r.data }))
        );
      } else {
        requests.push(Promise.resolve({ type: "staff", data: null }));
      }

      const results = await Promise.allSettled(requests);

      let logResult = null;
      let staffResult = null;
      const errors = [];

      for (const result of results) {
        if (result.status === "rejected") {
          const err = result.reason;
          errors.push(
            err?.response?.data?.message || err?.message || "Unknown error"
          );
          continue;
        }

        const { type, data } = result.value;

        if (type === "log" && data?.success) {
          logResult = data.data;
        } else if (type === "staff" && data?.success) {
          staffResult = data.data;
        } else if (data && !data?.success) {
          errors.push(data?.message);
        }
      }

      if (!logResult && !staffResult && errors.length) {
        throw new Error(errors.join(" | "));
      }

      setLogDetail(logResult);
      setStaffDetail(staffResult);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "An unexpected error occurred.";
      setDetailError(msg);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleCloseModal = () => {
    setOpenModal(false);
    setStaffDetail(null);
    setLogDetail(null);
    setDetailError(null);
  };

  // ── Table loading ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="rounded-lg bg-white p-8 shadow-sm">
        <div className="flex items-center justify-center">
          <div
            className="h-8 w-8 animate-spin rounded-full border-4"
            style={{ borderColor: `${BRAND}33`, borderTopColor: BRAND }}
          />
          <span className="ml-3 text-sm text-gray-600 sm:text-base">
            Loading activities…
          </span>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="overflow-hidden rounded-lg">
      {/* ── Responsive Table Wrapper ─────────────────────────────────────── */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 bg-gray-100">
          <thead>
            <tr className="bg-[#e0ced7]">
              {[
                "SL No",
                "Timestamp",
                "Staff Name",
                "Action",
                "Description",
              ].map((h) => (
                <th
                  key={h}
                  className="text-black px-3 py-3 text-center text-[11px] font-bold uppercase tracking-wider sm:px-4 sm:py-3.5 sm:text-[12px] lg:px-6 lg:text-[13px]"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200 bg-white">
            {activities.length > 0 ? (
              activities.map((activity, index) => (
                <tr
                  key={activity.id}
                  onClick={() => openActivityModal(activity)}
                  className="group cursor-pointer text-center transition-all duration-200 hover:bg-[#c51369]"
                >
                  {/* SL No */}
                  <td className="border px-3 py-2.5 text-xs text-gray-900 group-hover:text-white sm:px-4 sm:py-3 sm:text-sm lg:px-6">
                    {(currentPage - 1) * 5 + index + 1}
                  </td>

                  {/* Timestamp */}
                  <td className="border px-3 py-2.5 text-xs sm:px-4 sm:py-3 sm:text-sm lg:px-6">
                    <div className="font-medium text-gray-900 group-hover:text-white">
                      {new Date(activity.timestamp).toLocaleDateString(
                        "en-GB",
                        {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        }
                      )}
                    </div>
                    <div className="mt-0.5 text-[10px] text-gray-500 group-hover:text-white/80 sm:text-xs">
                      {new Date(activity.timestamp).toLocaleTimeString(
                        "en-GB",
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        }
                      )}
                    </div>
                  </td>

                  {/* Staff Name */}
                  <td className="border px-3 py-2.5 text-xs text-gray-900 group-hover:text-white sm:px-4 sm:py-3 sm:text-sm lg:px-6">
                    <div className="font-medium">
                      {activity.staffName || "—"}
                    </div>
                    <div className="mt-0.5 text-[10px] text-gray-400 group-hover:text-white/80 sm:text-xs">
                      {activity.phoneNumber || "—"}
                    </div>
                  </td>

                  {/* Action */}
                  <td className="border px-3 py-2.5 text-xs sm:px-4 sm:py-3 sm:text-sm lg:px-6">
                    <span className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-800 transition-all duration-200 group-hover:bg-white group-hover:text-[#c51369] sm:px-2.5 sm:py-1 sm:text-xs">
                      {activity.action}
                    </span>
                  </td>

                  {/* Description */}
                  <td className="border px-3 py-2.5 text-left text-xs text-gray-900 group-hover:text-white sm:px-4 sm:py-3 sm:text-sm lg:px-6">
                    <DescriptionCell
                      description={activity.description}
                      onReadMore={() => openActivityModal(activity)}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="5"
                  className="py-10 text-center text-sm text-gray-500"
                >
                  No activities found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {activities.length > 0 && (
        <div className="border-t border-gray-200 px-3 py-3 sm:px-6">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            showFirstLast={true}
          />
        </div>
      )}

      {/* Activity + Staff Detail Modal */}
      <ModalWrapper isOpen={openModal} onClose={handleCloseModal}>
        {/*
          • Mobile  (< sm): anchored to bottom, full width, rounded top corners
          • Tablet+ (≥ sm): centred card, max-w-lg/xl, fully rounded, shadow-2xl
        */}
        <div
          className="w-full overflow-hidden rounded-t-2xl bg-gray-50 shadow-2xl sm:rounded-2xl"
          style={{ maxHeight: "88vh" }}
        >
          <ActivityDetailModal
            staffData={staffDetail}
            logData={logDetail}
            loading={detailLoading}
            error={detailError}
          />
        </div>
      </ModalWrapper>
    </div>
  );
};

export default StaffActivityTable;
